import { Box, BoxProps, ButtonNew, ButtonProps, ColorOptions, Icon, Spacer, Text, TextNew } from '@jfs/ui-new'
import { FC, useEffect } from 'react'
import { CircularProgress } from '../../comps/CircularProgress'
import { useLazyApiData, useStores } from '../../data/store'

interface UploadFileProgressProps {
  hide?: Boolean
  progress?: number
  fileName?: any
  onHandleCancelChangeFile?: any
  navToHistory?: any
  mid?: string
  file?: any
  resetState?: any
}

const UploadFileProgress: FC<UploadFileProgressProps> = ({
  hide = false,
  progress = 0,
  fileName,
  onHandleCancelChangeFile,
  navToHistory,
  mid,
  file,
  resetState,
}: UploadFileProgressProps) => {
  const {
    uploadBulkRefundFile,
    setFileUploadProgress,
    setUploadStatus,
    uploadStatus,
    count,
    size,
    getBulkRefundUploadConfirm,
    uploadedFileId,
  } = useStores((store) => ({
    uploadBulkRefundFile: store?.bulkRefund?.uploadBulkRefundFile,
    uploadStatus: store?.bulkRefund?.uploadStatus,
    setUploadStatus: store?.bulkRefund?.setUploadStatus,
    count: store?.bulkRefund?.count,
    size: store?.bulkRefund?.size,
    set: store?.bulkRefund?.size,
    setFileUploadProgress: store?.settings?.metaData?.setFileUploadProgress,
    getBulkRefundUploadConfirm: store?.bulkRefund?.getBulkRefundUploadConfirm,
    uploadedFileId: store?.bulkRefund?.uploadedFileId,
  }))

  const [uploadFile, uploadFileHandler] = useLazyApiData(uploadBulkRefundFile)
  const [uploadConfirm, uploadConfirmHandler] = useLazyApiData(getBulkRefundUploadConfirm)

  const btnStyle = uploadStatus === 'UPLOADED' ? styles.uploadButton : styles.proceedButton
  /** Upload File API Handler */
  useEffect(() => {
    if (uploadFileHandler?.state === 'fulfilled' && uploadFileHandler?.data?.status === 'FAILED') {
      resetState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileHandler, uploadFileHandler.state, uploadFileHandler?.data?.status])

  useEffect(() => {
    if (uploadStatus !== 'UPLOADED' && uploadStatus !== 'PROCESSED') {
      uploadFile(mid, fileName, size, file)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, fileName, mid, size])

  useEffect(() => {
    if (uploadConfirmHandler?.state === 'fulfilled' && uploadConfirmHandler?.data?.status === 'CONFIRMED') {
      setUploadStatus('PROCESSED')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadConfirmHandler])

  if (hide) {
    return null
  }

  const uploadFileAndProcess = (_evt?: any) => {
    if (uploadStatus === 'UPLOADED') {
      uploadConfirm(uploadedFileId)
      setFileUploadProgress(0)
      setUploadStatus('IN_PROCESS')
      setTimeout(() => {
        setFileUploadProgress(100)
      }, 1000)
    } else {
      navToHistory()
    }
  }

  return (
    <Box {...styles.container}>
      <Box {...styles.content}>
        {(uploadStatus === 'IN_PROGRESS' || uploadStatus === 'IN_PROCESS') && (
          <CircularProgress percentage={progress} color={'#0186B0'}>
            <Box {...styles.progressView}>
              <TextNew title={progress + '%'} color={'PRIMARY'} fontFamily={'jiotype-medium'} fontSize={14} />
            </Box>
          </CircularProgress>
        )}
        {uploadStatus === 'UPLOADED' && (
          <Box {...styles.fileContainer}>
            <Icon color={'BLACK_21'} id={'SETTLEMENTS'} size={24} />
          </Box>
        )}
        {uploadStatus === 'PROCESSED' && (
          <Box {...styles.filledStyle}>
            <Icon id={'TICKMARK'} color={'#3F8C3A' as ColorOptions} size={24} />
          </Box>
        )}
        <Box {...styles.contentContainer}>
          <Text
            fontFamily={'jiotype-medium'}
            fontSize={12}
            color={'BRAND_COLOR_GRAY_1'}
            testID="bulkRefundFileUploadStatusMsg">
            {uploadStatus === 'IN_PROGRESS'
              ? 'File upload in progress…'
              : uploadStatus === 'UPLOADED'
              ? fileName ?? ''
              : uploadStatus === 'IN_PROCESS'
              ? 'File upload in progress…'
              : uploadStatus === 'PROCESSED'
              ? 'Refund request initiated successfully'
              : null}
          </Text>
          <Spacer size={16} />
          <Text fontFamily={'jiotype-bold'} fontSize={14} color={'BLACK_21'}>
            {uploadStatus === 'UPLOADED' || uploadStatus === 'PROCESSED' ? 'FileName-' + fileName : ' '}
          </Text>
          <Spacer size={8} />
          <Box direction={'row'}>
            <Text fontFamily={'jiotype-medium'} fontSize={14} color={'BLACK_75'}>
              {uploadStatus === 'UPLOADED' || uploadStatus === 'PROCESSED' ? 'Refund Count : ' : ' '}
            </Text>
            <Text fontFamily={'jiotype-bold'} fontSize={14} color={'BLACK_21'}>
              {uploadStatus === 'UPLOADED' || uploadStatus === 'PROCESSED' ? count : ' '}
            </Text>
          </Box>
          <Spacer size={24} />
          <Text
            hide={uploadStatus !== 'PROCESSED'}
            fontFamily={'jiotype-medium'}
            fontSize={14}
            color={'BLACK_75'}
            lineHeight={22}
            textAlign={'center'}>
            {
              'The refund will be credited to the source account of the user within 3 - 5 business days,\nYou can track the status of this refund in '
            }
            <Text
              title={'Refund History'}
              fontFamily={'jiotype-medium'}
              fontSize={14}
              color={'BLUE'}
              testID="upload-file"
              onPress={navToHistory}
              textDecorationLine={'underline'}
            />
          </Text>
        </Box>
        <Spacer size={32} />
        {uploadStatus === 'UPLOADED' && (
          <Box {...styles.buttonContainer}>
            <ButtonNew
              {...styles.cancelButton}
              title={'Cancel'}
              testID="cancel"
              onPress={onHandleCancelChangeFile}
              borderWidth={1}
              borderColor={'BLUE'}
            />
            <Spacer size={24} direction={'horizontal'} />
            <ButtonNew
              testID={`uploadFileToProcess${uploadStatus}`}
              {...btnStyle}
              title={'Upload file & Process Refund'}
              onPress={uploadFileAndProcess}
            />
          </Box>
        )}
        {uploadStatus === 'PROCESSED' && (
          <ButtonNew
            {...styles.cancelButton}
            title={'Done'}
            onPress={onHandleCancelChangeFile}
            borderWidth={1}
            borderColor={'BLUE'}
          />
        )}
        {uploadStatus === 'IN_PROGRESS' && <Box height={32} />}
      </Box>
      <Spacer size={40} direction={'vertical'} />
    </Box>
  )
}

const styles = {
  container: { width: '100%', align: 'center', justify: 'center' } as BoxProps,
  content: {
    width: '700px',
    align: 'center',
    justify: 'center',
  } as BoxProps,
  progressView: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    width: 80,
    height: 80,
    borderRadius: 40,
    borderColor: '#3F8C3A33' as ColorOptions,
    alignItems: 'center',
    zIndex: 10000,
    justifyContent: 'center',
  } as BoxProps,
  filledStyle: {
    width: '72px',
    height: '72px',
    borderRadius: 36,
    borderColor: '#3F8C3A' as ColorOptions,
    borderWidth: 1,
    alignItems: 'center',
    zIndex: 10000,
    justifyContent: 'center',
    backgroundColor: '#3F8C3A14' as ColorOptions,
  } as BoxProps,
  fileContainer: {
    width: '80px',
    height: '80px',
    borderRadius: 40,
    borderWidth: 1,
    borderColor: '#3F8C3A33' as ColorOptions,
    alignItems: 'center',
    backgroundColor: '#3F8C3A14' as ColorOptions,
    justifyContent: 'center',
  } as BoxProps,
  contentContainer: {
    direction: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 24,
  } as BoxProps,
  buttonContainer: {
    direction: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  } as BoxProps,
  cancelButton: {
    borderColor: 'TRANSPARENT',
    shadowType: 'shadowPrimary',
    titleColor: 'PRIMARY',
    type: 'secondarySmall',
  } as ButtonProps,
  proceedButton: {
    borderColor: 'TRANSPARENT',
    shadowType: 'shadowPrimary',
    titleColor: 'WHITE',
    type: 'primaryIconSmall',
    borderRadius: 6,
  } as ButtonProps,
  uploadButton: {
    borderColor: 'TRANSPARENT',
    shadowType: 'shadowPrimary',
    titleColor: 'WHITE',
    type: 'primaryIconSmall',
    borderRadius: 6,
  } as ButtonProps,
}
export { UploadFileProgress }
